import { Goal, Insights, InsightSections } from '@griply/types';
import { isBefore, isSameDay } from 'date-fns';

import { sortItems } from '../sort-items';

export type GetInsightsGoalsOptions = {
  goals: Goal[];
  today: Date;
  insightsSettings: Insights | null | undefined;
};

export const getInsightsGoals = ({
  goals,
  today,
  insightsSettings,
}: GetInsightsGoalsOptions): Goal[] => {
  const goalsSectionSettings =
    insightsSettings?.sections?.[InsightSections.Goals];
  const showHiddenItems = insightsSettings?.showHiddenItems ?? false;

  const isVisible = (id: Goal['id']) =>
    showHiddenItems || !goalsSectionSettings?.hiddenItems?.includes(id);

  const isDateActive = (startDate: Goal['startDate']) =>
    !!startDate && (isBefore(startDate, today) || isSameDay(startDate, today));

  // Filter active main goals from sorted goals
  let sortedActiveGoals = goals.filter(
    ({ archivedAt, completedAt, parentIds }) =>
      !archivedAt && !completedAt && !parentIds?.length,
  );

  // Apply custom order if present
  if (goalsSectionSettings?.order?.length) {
    sortedActiveGoals = sortItems(
      sortedActiveGoals,
      goalsSectionSettings.order,
      'id',
    );
  }

  // Add active subGoals recursively if enabled
  if (insightsSettings?.showActiveSubGoals) {
    const processActiveGoalAndSubGoals = (goal: Goal): Goal[] => {
      const goalIsActive =
        isVisible(goal.id) &&
        !goal.archivedAt &&
        !goal.completedAt &&
        isDateActive(goal.startDate);
      const results = goalIsActive ? [goal] : [];

      const subGoals = goals.filter(
        ({ parentIds, archivedAt, completedAt }) =>
          !archivedAt &&
          !completedAt &&
          parentIds?.[parentIds.length - 1] === goal.id,
      );

      results.push(
        ...subGoals
          .map((subGoal) => processActiveGoalAndSubGoals(subGoal))
          .flat(1),
      );

      return results;
    };

    return sortedActiveGoals.reduce<Goal[]>((acc, goal) => {
      acc.push(...processActiveGoalAndSubGoals(goal));
      return acc;
    }, []);
  }

  // When no subGoals have to be shown, filter out inactive goals
  return sortedActiveGoals.filter(
    ({ id, startDate }) => isVisible(id) && isDateActive(startDate),
  );
};
